import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, Send, Sparkles, Trash2, X } from 'lucide-react';

import { iceLogo } from '../mock';
import { clearCoachThread } from '../lib/api';
import { CoachModelSelector } from './CoachModelSelector';
import {
  CHAT_ASSISTANT_KEY,
  CHAT_THREADS_KEY,
  COACH_ASSISTANTS,
  getSessionId,
  loadAssistant,
  loadThreads,
} from './coachChatConfig';

const suggestions = [
  'When are U9 practices?',
  'How do I register my scholar?',
  'What time is the Family Barbecue?',
  'Who coaches the U13 team?',
];

export const CoachChat = () => {
  const [open, setOpen] = useState(false);
  const [assistant, setAssistant] = useState(loadAssistant);
  const [threads, setThreads] = useState(loadThreads);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());
  const messages = threads[assistant];
  const assistantInfo = COACH_ASSISTANTS[assistant];
  const canClear = messages.length > 1 && !busy;

  useEffect(() => { setConfirmClear(false); }, [assistant, open]);

  const clearThread = async () => {
    const target = assistant;
    setConfirmClear(false);
    updateThread(target, () => [{ role: 'assistant', content: COACH_ASSISTANTS[target].greeting }]);
    try {
      await clearCoachThread(sessionId.current, target);
    } catch {
      // Local thread is already reset; server history will be superseded by the next message.
    }
  };

  useEffect(() => {
    localStorage.setItem(CHAT_ASSISTANT_KEY, assistant);
  }, [assistant]);

  useEffect(() => {
    localStorage.setItem(CHAT_THREADS_KEY, JSON.stringify(threads));
  }, [threads]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (!open || window.innerWidth >= 768) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  const updateThread = (target, updater) => {
    setThreads((current) => ({ ...current, [target]: updater(current[target]) }));
  };

  const applyStreamEvent = (target, event) => {
    if (event.type === 'delta') {
      updateThread(target, (current) => {
        const copy = [...current];
        const last = copy[copy.length - 1];
        copy[copy.length - 1] = { ...last, content: `${last.content || ''}${event.content}` };
        return copy;
      });
    }
    if (event.type === 'error') {
      updateThread(target, (current) => {
        const copy = [...current];
        copy[copy.length - 1] = { role: 'assistant', content: 'Sorry — I had a hiccup. Please try again shortly.', error: true };
        return copy;
      });
    }
  };

  const send = async (event) => {
    event?.preventDefault();
    const question = input.trim();
    if (!question || busy) return;
    const target = assistant;
    setInput('');
    updateThread(target, (current) => [
      ...current,
      { role: 'user', content: question },
      { role: 'assistant', content: '' },
    ]);
    setBusy(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coach/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId.current, message: question, assistant: target }),
      });
      if (!response.ok) throw new Error(`Request failed with ${response.status}`);
      if (!response.body) throw new Error('No response stream');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        lines.filter((line) => line.startsWith('data: ')).forEach((line) => {
          try {
            applyStreamEvent(target, JSON.parse(line.slice(6)));
          } catch {
            // Ignore malformed stream fragments and keep reading.
          }
        });
      }
    } catch {
      applyStreamEvent(target, { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="coach-chat-open"
          className="group fixed bottom-[calc(0.75rem+env(safe-area-inset-bottom))] right-3 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-[#c8102e] text-white shadow-2xl transition-transform hover:scale-105 hover:bg-[#e11d34] sm:bottom-6 sm:right-6 sm:h-16 sm:w-16"
          aria-label="Ask a Coach"
        >
          <MessageCircle className="h-7 w-7" aria-hidden="true" />
          <span className="absolute -right-2 -top-2 whitespace-nowrap rounded-full bg-[#0a1f44] px-2 py-0.5 font-heading text-[10px] uppercase tracking-widest text-white opacity-0 transition-opacity group-hover:opacity-100">Ask a Coach</span>
        </button>
      )}

      {open && (
        <div className="fixed inset-x-0 bottom-0 z-50 flex h-[min(82dvh,640px)] max-h-[min(82dvh,640px)] flex-col overflow-hidden border-t-2 border-[#0a1f44] bg-white pb-[env(safe-area-inset-bottom)] shadow-2xl md:inset-auto md:bottom-6 md:right-6 md:h-auto md:w-[400px] md:border-2" data-testid="coach-chat-panel" role="dialog" aria-modal="true" aria-label="Ask a Coach" aria-busy={busy}>
          <div className="flex items-center gap-3 bg-[#0a1f44] px-4 py-3 text-white">
            <img src={iceLogo} alt="ICE" className="h-8 w-8 object-contain" data-testid="coach-chat-logo" />
            <div className="min-w-0 flex-1">
              <div className="truncate font-display text-lg leading-none tracking-wide" data-testid="coach-assistant-name">{assistantInfo.name.toUpperCase()}</div>
              <div className="mt-1 flex items-center gap-1 font-heading text-[9px] uppercase tracking-[0.2em] text-[#f05a72]" data-testid="coach-assistant-model">
                <Sparkles className="h-3 w-3" aria-hidden="true" /> Ask a Coach · {assistantInfo.modelLabel}
              </div>
            </div>
            <button onClick={() => setConfirmClear(true)} disabled={!canClear} data-testid="coach-chat-clear" className="flex h-11 w-11 items-center justify-center text-white/70 transition-colors hover:text-white disabled:opacity-30" aria-label={`Clear ${assistantInfo.name} conversation`} title="Clear conversation">
              <Trash2 className="h-5 w-5" aria-hidden="true" />
            </button>
            <button onClick={() => setOpen(false)} data-testid="coach-chat-close" className="flex h-11 w-11 items-center justify-center text-white/70 transition-colors hover:text-white" aria-label="Close Ask a Coach">
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <CoachModelSelector activeAssistant={assistant} busy={busy} onChange={setAssistant} />

          {confirmClear && (
            <div className="flex items-center justify-between gap-3 border-b border-[#c8102e]/30 bg-[#c8102e]/10 px-4 py-2 text-xs text-[#0a1f44]" data-testid="coach-chat-clear-confirm" role="alertdialog" aria-label="Confirm clearing conversation">
              <span>Clear your {assistantInfo.name} thread? Your {COACH_ASSISTANTS[assistant === 'claude' ? 'chatgpt' : 'claude'].name} thread stays.</span>
              <div className="flex shrink-0 gap-1">
                <button onClick={clearThread} data-testid="coach-chat-clear-yes" className="bg-[#c8102e] px-3 py-1.5 font-heading text-[10px] uppercase tracking-widest text-white hover:bg-[#e11d34]">Clear</button>
                <button onClick={() => setConfirmClear(false)} data-testid="coach-chat-clear-no" className="border border-[#0a1f44]/30 px-3 py-1.5 font-heading text-[10px] uppercase tracking-widest text-[#0a1f44]">Keep</button>
              </div>
            </div>
          )}

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-[#f5f6f8] p-4" data-testid="coach-chat-messages" role="log" aria-live="polite" aria-busy={busy}>
            {messages.map((message, index) => (
              <div key={`${message.role}-${index}`} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  data-testid={`coach-chat-message-${assistant}-${index}`}
                  className={`max-w-[85%] whitespace-pre-wrap px-4 py-2.5 text-left text-sm leading-relaxed ${message.role === 'user' ? 'bg-[#c8102e] text-white' : message.error ? 'border border-[#c8102e]/30 bg-[#fff4f5] text-[#7d1022]' : 'border border-[#0a1f44]/10 bg-white text-[#0a1f44]'}`}
                >
                  {message.content || (busy && index === messages.length - 1 ? <span className="text-[#0a1f44]/40" data-testid="coach-chat-loading">Thinking…</span> : '')}
                </div>
              </div>
            ))}

            {messages.length === 1 && (
              <div className="space-y-2 pt-2" data-testid={`coach-suggestions-${assistant}`}>
                <div className="px-1 font-heading text-[10px] uppercase tracking-widest text-[#0a1f44]/60">Try asking:</div>
                {suggestions.map((suggestion, index) => (
                  <button
                    key={suggestion}
                    onClick={() => setInput(suggestion)}
                    data-testid={`coach-suggestion-${assistant}-${index}`}
                    className="min-h-11 w-full border border-[#0a1f44]/10 bg-white px-3 py-2 text-left text-sm text-[#0a1f44]/80 transition-colors hover:border-[#c8102e]"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={send} className="flex gap-2 border-t border-[#0a1f44]/10 bg-white p-3" data-testid="coach-chat-form">
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={`Ask ${assistantInfo.shortLabel}…`}
              disabled={busy}
              maxLength={1600}
              autoComplete="off"
              data-testid="coach-chat-input"
              className="min-w-0 flex-1 border border-[#0a1f44]/20 px-3 py-2 text-base focus:border-[#c8102e] focus:outline-none"
              aria-label={`Message ${assistantInfo.name}`}
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              data-testid="coach-chat-send"
              className="flex min-w-12 items-center justify-center bg-[#c8102e] px-4 text-white transition-colors hover:bg-[#e11d34] disabled:opacity-40"
              aria-label={`Send to ${assistantInfo.name}`}
            >
              <Send className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};